import React, { useEffect, useState } from "react";
import "./AllProductsPage.css";
import { Grid, Pagination } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import BACKOFFICE_BASE_API_V1 from "../../utils/backofficeApi";
import { ensureOkResponse, handleApiFailureRedirect } from "../../utils/apiFailsafe";

function AllProductsPage() {
  const navigate = useNavigate();
  const DEFAULT_PRODUCT_IMAGE =
    "https://placehold.co/600x400/weddings/white?text=Kazhavda";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedSort, setSelectedSort] = useState("created_at");
  const [selectedOrder, setSelectedOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage, setPerPage] = useState(12);

  const formatPrice = (price) => {
    const parsedPrice = Number(price);
    if (!Number.isFinite(parsedPrice)) {
      return "—";
    }

    return Math.trunc(parsedPrice).toLocaleString("mk-MK");
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchProducts() {
      if (!isMounted) {
        return;
      }
      setIsFetching(true);
      try {
        const response = await fetch(
          `${BACKOFFICE_BASE_API_V1}/products/all_products?sort_by=${selectedSort}&order=${selectedOrder}&available=true&page=${currentPage}&per_page=${perPage}`
        );
        ensureOkResponse(response);
        const data = await response.json();
        const nextProducts = Array.isArray(data?.products) ? data.products : [];
        const pagination = data?.pagination || {};
        const parsedPage = Number(pagination.page);
        const parsedPerPage = Number(pagination.per_page);
        const parsedTotalCount = Number(pagination.total_count);
        const parsedTotalPages = Number(
          pagination.total_pages ||
            data?.total_pages ||
            data?.meta?.total_pages ||
            data?.meta?.pagination?.total_pages ||
            1
        );

        if (!isMounted) {
          return;
        }

        setProducts(nextProducts);
        if (Number.isFinite(parsedPage) && parsedPage > 0 && parsedPage !== currentPage) {
          setCurrentPage(parsedPage);
        }
        if (Number.isFinite(parsedPerPage) && parsedPerPage > 0 && parsedPerPage !== perPage) {
          setPerPage(parsedPerPage);
        }
        setTotalCount(Number.isFinite(parsedTotalCount) && parsedTotalCount >= 0 ? parsedTotalCount : 0);
        setTotalPages(
          Number.isFinite(parsedTotalPages) && parsedTotalPages > 0
            ? parsedTotalPages
            : 1
        );
      } catch (error) {
        if (isMounted) {
          handleApiFailureRedirect(navigate, error);
        }
      } finally {
        if (isMounted) {
          setIsFetching(false);
          setLoading(false);
        }
      }
    }
    fetchProducts();
    return () => {
      isMounted = false;
    };
  }, [selectedSort, selectedOrder, currentPage, perPage, navigate]);

  const updateSorting = (sortBy, order) => {
    setSelectedSort(sortBy);
    setSelectedOrder(order);
    setCurrentPage(1);
  };

  const updatePerPage = (event) => {
    setPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
    const pageTopElement = document.querySelector(".all-products-page");
    if (!pageTopElement) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const navbarOffset = 88;
    const targetTop =
      pageTopElement.getBoundingClientRect().top + window.scrollY - navbarOffset;
    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: "smooth",
    });
  };

  if (loading) {
    return <div className="loading">Вчитување...</div>;
  }

  return (
    <div className="all-products-page">
      <h1 className="all-products-title">СИТЕ ПРОИЗВОДИ</h1>
      <div className="products-meta-row">
        <div className="products-count-label">
          Вкупно: {totalCount}
        </div>
        <div className="per-page-control">
          <label htmlFor="products-per-page">ПРИКАЖИ:</label>
          <select
            id="products-per-page"
            value={perPage}
            onChange={updatePerPage}
          >
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
          </select>
        </div>
      </div>
      {/* Sorting Options */}
      <div className="sorting-options">
        <span>СОРТИРАЈ СПОРЕД:</span>
        <span
          className={selectedSort === "created_at" ? "active" : ""}
          onClick={() => updateSorting("created_at", "desc")}
          >
          НАЈНОВО
        </span>
        <span
          className={selectedSort === "price" && selectedOrder === "asc" ? "active" : ""}
          onClick={ () => updateSorting("price", "asc")}
        >
          РАСТЕЧКА ЦЕНА
        </span>
        <span
          className={selectedSort === "price" && selectedOrder === "desc" ? "active" : ""}
          onClick={() => updateSorting("price", "desc")}
        >
          ОПАЃАЧКА ЦЕНА
        </span>
      </div>

      <div className="all-products-container">
        <Grid
          className="all-products-list"
          container
          spacing={2}
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { xs: "center", md: "flex-start" },
            alignItems: { xs: "center" },
            px: { xs: 2, sm: 4 },
          }}
        >
          {products.length > 0 ? (
            products.map((product) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={product.id}>
                <Link to={`/product/${product.id}?viewed_from=standard`} className="product-card-link">
                  <div className="product-card">
                    <div className="product-image-wrap">
                      <img
                        src={product.images?.[0] || DEFAULT_PRODUCT_IMAGE}
                        alt={product.name}
                        className="product-image"
                        onError={(event) => {
                          const imageElement = event.currentTarget;
                          imageElement.onerror = null;
                          imageElement.src = DEFAULT_PRODUCT_IMAGE;
                        }}
                      />
                    </div>
                    <div className="product-card-content">
                      <div className="product-card-content-title">
                      <h3 className="product-title">{product.name}</h3>
                      <p className="product-price">
                        {formatPrice(product.price)},00<small>ден</small>
                      </p>
                      </div>
                      <p className="product-card-venue">{product.venue_name}</p>
                      <p className="product-card-description">
                        {product.description}
                      </p>
                    </div>
                  </div>
                </Link>
              </Grid>
            ))
          ) : (
            <Grid size={12}>
              <div className="no-products-message">
                Нема места во оваа категорија засега...
              </div>
            </Grid>
          )}
        </Grid>

        {totalPages > 1 && (
          <div className="all-products-pagination">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              shape="rounded"
              siblingCount={0}
              boundaryCount={1}
              disabled={isFetching}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default AllProductsPage;
